import React, { useState, useMemo } from 'react'
import config from '../../config.js'

const API = config.API_BASE_URL

function secsToHMS(totalSec) {
  if (!totalSec) return '00:00:00'
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = Math.floor(totalSec % 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function nesterFileToProductId(fileName) {
  if (!fileName) return ''
  return fileName.replace(/\.dxf$/i, '').replace(/\s+/g, '-').toUpperCase()
}

function findMaterial(allMaterials, matType, thicknessId, surfaceFinish, sheetW, sheetL, customerSupplied, custId) {
  if (!matType || !thicknessId) return null
  return allMaterials.find(m => {
    if (m.material_type !== matType) return false
    if (m.thickness_id !== parseInt(thicknessId)) return false
    if (surfaceFinish && m.surface_finish !== surfaceFinish) return false
    if (!surfaceFinish && m.surface_finish) return false
    if (sheetW && m.width && Math.abs(m.width - parseFloat(sheetW)) > 0.1) return false
    if (sheetL && m.length && Math.abs(m.length - parseFloat(sheetL)) > 0.1) return false
    if (customerSupplied && custId && m.owner_customer_id !== parseInt(custId)) return false
    return true
  }) || null
}

function buildSheetRows(nestResult, project, materialGroups, allMaterials, allParts, thicknesses,
  projMatType, projThickDisp, projThickIn, projSF, projCustId, projSheetW, projSheetL) {

  const rows = []

  // Determine if multi-group nest
  const groupResults = nestResult.group_results || []
  const nestSources = groupResults.length > 0
    ? groupResults.map(gr => ({ result: gr, group: materialGroups?.find(g => g.group_id === gr.material_group_id) }))
    : [{ result: nestResult, group: null }]

  for (const { result: nr, group } of nestSources) {
    const sheetGroups = nr.sheet_groups || []
    const sheets = nr.detailed_estimate?.sheets || []
    if (sheetGroups.length === 0 || sheets.length === 0) continue

    // Material props from group or project
    const matType = group?.material_type || projMatType || ''
    const thickDisp = group?.thickness_display || projThickDisp || ''
    const surfFinish = group?.surface_finish || projSF || ''
    const sheetW = group?.sheet_width || projSheetW || ''
    const sheetL = group?.sheet_length || projSheetL || ''
    const custSupplied = group?.customer_supplied_material || false
    const custId = projCustId || ''
    const groupName = group?.group_name || ''

    // Find thickness_id
    const matchedThickness = thicknesses.find(t =>
      t.display_thickness === thickDisp &&
      t.material_type?.toLowerCase() === matType.toLowerCase()
    )
    const thickId = matchedThickness?.thickness_id || null

    // Material match
    const materialMatch = findMaterial(allMaterials, matType, thickId, surfFinish, sheetW, sheetL, custSupplied, custId)

    for (let gi = 0; gi < sheetGroups.length; gi++) {
      const sg = sheetGroups[gi]
      const sheetData = sheets.find(s => s.sheet_number === sg.template_sheet)
      if (!sheetData) continue

      // Map parts
      const partsMapped = (sheetData.parts_detail || []).map(pd => {
        const productId = nesterFileToProductId(pd.file_name)
        const jobqPart = allParts.find(p => p.product_id === productId)
        return {
          productId,
          partId: jobqPart?.part_id || null,
          partLabel: pd.part_label || pd.file_name,
          qtyPerSheet: pd.quantity_on_sheet || 0,
          warning: jobqPart ? null : `Part "${productId}" not found in JobQ`,
        }
      })

      const unmappedParts = partsMapped.filter(p => !p.partId)
      const totalCutSec = (sheetData.cut_path_sec || 0) + (sheetData.sh_cut_sec || 0) + (sheetData.pierce_sec || 0)

      // Auto-name
      const projName = project?.project_name || 'Cut Sheet'
      let sheetName
      if (nestSources.length === 1 && sheetGroups.length === 1) {
        sheetName = projName
      } else if (nestSources.length > 1) {
        sheetName = sheetGroups.length === 1
          ? `${projName} - ${groupName}`
          : `${projName} - ${groupName} - Sheet ${gi + 1}`
      } else {
        sheetName = `${projName} - Sheet ${gi + 1}`
      }

      // Description
      const desc = [matType, thickDisp, sheetW && sheetL ? `${sheetW}x${sheetL}` : ''].filter(Boolean).join(' ')

      rows.push({
        groupIdx: gi,
        groupId: group?.group_id || null,
        groupName,
        templateSheet: sg.template_sheet,
        count: sg.count,
        cutSheetName: sheetName,
        description: desc,
        materialId: materialMatch?.material_id || null,
        materialMatch,
        materialWarning: materialMatch ? null : `No material match: ${matType} ${thickDisp} ${surfFinish || ''} ${sheetW}x${sheetL}`.trim(),
        customerId: custId ? parseInt(custId) : null,
        cutPathTime: secsToHMS(sheetData.cut_path_sec),
        shCutTime: secsToHMS(sheetData.sh_cut_sec),
        pierceTime: secsToHMS(sheetData.pierce_sec),
        totalCutTime: secsToHMS(totalCutSec),
        shuttleTime: secsToHMS(sheetData.reposition_sec),
        laserMinutes: (sheetData.total_sec || 0) / 60,
        parts: partsMapped,
        partsWarning: unmappedParts.length > 0 ? `${unmappedParts.length} part(s) not found in JobQ` : null,
        selected: true,
        status: 'pending',
        error: null,
      })
    }
  }

  return rows
}

export default function CreateCutSheetsModal({
  show, onClose, nestResult, project, materialGroups, allMaterials, allParts,
  customers, thicknesses,
  projectMaterialType, projectThicknessDisplay, projectThicknessIn,
  projectSurfaceFinish, projectCustomerId, projectSheetWidth, projectSheetLength,
}) {
  const initialRows = useMemo(() => {
    if (!nestResult) return []
    return buildSheetRows(nestResult, project, materialGroups, allMaterials, allParts, thicknesses,
      projectMaterialType, projectThicknessDisplay, projectThicknessIn,
      projectSurfaceFinish, projectCustomerId, projectSheetWidth, projectSheetLength)
  }, [nestResult, project, materialGroups, allMaterials, allParts, thicknesses,
      projectMaterialType, projectThicknessDisplay, projectThicknessIn,
      projectSurfaceFinish, projectCustomerId, projectSheetWidth, projectSheetLength])

  const [rows, setRows] = useState(initialRows)
  const [creating, setCreating] = useState(false)

  function updateRow(idx, field, value) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }

  function toggleAll() {
    const allSelected = rows.every(r => r.selected || r.status === 'created')
    setRows(prev => prev.map(r => r.status === 'created' ? r : { ...r, selected: !allSelected }))
  }

  const canCreate = (row) => row.materialId && !row.partsWarning && row.customerId

  async function createCutSheets() {
    setCreating(true)
    const toCreate = rows.filter(r => r.selected && r.status !== 'created' && canCreate(r))
    const useSet = toCreate.length > 1

    let setId = null

    // Create set header if multiple sheets
    if (useSet) {
      try {
        const setRes = await fetch(`${API}/cut_sheet_sets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            set_name: project?.project_name || 'Nester Cut Sheet Set',
            description: `Auto-generated from DXF nester — ${toCreate.length} sheet layout(s)`,
          }),
        })
        if (!setRes.ok) throw new Error(`Failed to create set: ${setRes.status}`)
        const setData = await setRes.json()
        setId = setData.cut_sheet_set_id
      } catch (err) {
        // Mark all as error
        setRows(prev => prev.map(r => r.selected && r.status !== 'created'
          ? { ...r, status: 'error', error: err.message } : r))
        setCreating(false)
        return
      }
    }

    for (let i = 0; i < toCreate.length; i++) {
      const row = toCreate[i]
      const idx = rows.findIndex(r => r.templateSheet === row.templateSheet && r.groupId === row.groupId)
      setRows(prev => prev.map((r, j) => j === idx ? { ...r, status: 'creating' } : r))

      try {
        const payload = {
          cut_sheet_name: row.cutSheetName,
          description: row.description,
          material_id: row.materialId,
          customer_id: row.customerId || null,
          active_status: true,
          is_temporary: true,
          cut_path_time: row.cutPathTime,
          sh_cut_time: row.shCutTime,
          pierce_time: row.pierceTime,
          total_cut_time: row.totalCutTime,
          shuttle_time: row.shuttleTime,
          initial_est_laser_minutes: parseFloat(row.laserMinutes.toFixed(2)),
          parts: row.parts.filter(p => p.partId).map(p => ({
            part_id: p.partId,
            quantity_per_sheet: p.qtyPerSheet,
          })),
          ...(setId ? { cut_sheet_set_id: setId, quantity_in_set: row.count } : {}),
        }

        const res = await fetch(`${API}/cut_sheets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })

        if (!res.ok) {
          const errText = await res.text()
          throw new Error(errText.includes('duplicate') || errText.includes('already exists')
            ? 'Cut sheet name already exists' : `Error ${res.status}`)
        }

        setRows(prev => prev.map((r, j) => j === idx ? { ...r, status: 'created', error: null } : r))
      } catch (err) {
        setRows(prev => prev.map((r, j) => j === idx ? { ...r, status: 'error', error: err.message } : r))
      }
    }
    setCreating(false)
  }

  const selectedCount = rows.filter(r => r.selected && r.status !== 'created').length
  const creatableCount = rows.filter(r => r.selected && r.status !== 'created' && canCreate(r)).length
  const createdCount = rows.filter(r => r.status === 'created').length
  const hasWarnings = rows.some(r => r.materialWarning || r.partsWarning || !r.customerId)

  if (!show) return null

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.header}>
          <h5 style={{ margin: 0, fontSize: '1rem' }}>Create Cut Sheets in JobQ</h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        <div style={s.body}>
          {/* Warnings */}
          {hasWarnings && (
            <div style={{ marginBottom: '0.5rem' }}>
              {rows.some(r => r.partsWarning) && (
                <div style={s.warning}>
                  Parts not found in JobQ — use "Create Parts" first, then reopen this modal.
                </div>
              )}
              {rows.some(r => r.materialWarning) && (
                <div style={s.warning}>
                  Material not found in JobQ — create the material on the Materials page, then reopen.
                  {rows.filter(r => r.materialWarning).map((r, i) => (
                    <div key={i} style={{ fontSize: '0.72rem', marginTop: '0.25rem' }}>{r.materialWarning}</div>
                  ))}
                </div>
              )}
              {rows.some(r => !r.customerId) && (
                <div style={s.warning}>
                  No customer set on project — select a customer on the nester page before creating cut sheets.
                </div>
              )}
            </div>
          )}

          {rows.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              {nestResult
                ? 'Nest data missing sheet details. Re-generate the nest to populate cut sheet data.'
                : 'No nest result available. Generate a nest first.'}
            </div>
          ) : (
            <table className="table table-sm" style={{ fontSize: '0.8rem', marginBottom: 0 }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ width: 30 }}>
                    <input type="checkbox"
                      checked={rows.every(r => r.selected || r.status === 'created')}
                      onChange={toggleAll} />
                  </th>
                  <th>Name</th>
                  <th>Material</th>
                  <th style={{ textAlign: 'right' }}>Parts</th>
                  <th style={{ textAlign: 'right' }}>Time</th>
                  <th style={{ textAlign: 'right' }}>Qty</th>
                  <th style={{ width: 90, textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`${row.groupId}-${row.templateSheet}`} style={{
                    opacity: row.status === 'created' ? 0.6 : 1,
                    backgroundColor: row.status === 'error' ? '#fef2f2'
                      : row.status === 'created' ? '#f0fdf4'
                      : (!canCreate(row) && row.selected) ? '#fffbeb'
                      : 'transparent',
                  }}>
                    <td>
                      <input type="checkbox" checked={row.selected}
                        disabled={row.status === 'created'}
                        onChange={e => updateRow(idx, 'selected', e.target.checked)} />
                    </td>
                    <td>
                      <input className="form-control form-control-sm" value={row.cutSheetName}
                        onChange={e => updateRow(idx, 'cutSheetName', e.target.value)}
                        disabled={row.status === 'created'}
                        style={{ fontSize: '0.78rem', padding: '2px 6px', fontWeight: 600 }} />
                    </td>
                    <td style={{ fontSize: '0.75rem' }}>
                      {row.materialMatch ? (
                        <span style={{ color: '#16a34a' }}>{row.materialMatch.material_code}</span>
                      ) : (
                        <span style={{ color: '#dc2626', fontWeight: 600 }} title={row.materialWarning}>No match</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: '0.78rem' }}>
                      {row.partsWarning ? (
                        <span style={{ color: '#d97706' }} title={row.partsWarning}>
                          {row.parts.filter(p => p.partId).length}/{row.parts.length}
                        </span>
                      ) : (
                        row.parts.length
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {row.laserMinutes > 0 ? `${Math.floor(row.laserMinutes)}:${String(Math.round((row.laserMinutes % 1) * 60)).padStart(2, '0')}` : '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontSize: '0.78rem', fontWeight: 600 }}>
                      {row.count > 1 ? `×${row.count}` : '1'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {row.status === 'pending' && !canCreate(row) && row.selected && (
                        <span style={{ color: '#d97706', fontSize: '0.72rem', fontWeight: 600 }}>Blocked</span>
                      )}
                      {row.status === 'pending' && (canCreate(row) || !row.selected) && (
                        <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>-</span>
                      )}
                      {row.status === 'creating' && (
                        <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
                      )}
                      {row.status === 'created' && (
                        <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>Created</span>
                      )}
                      {row.status === 'error' && (
                        <span style={{ color: '#dc2626', fontSize: '0.72rem' }} title={row.error}>
                          {row.error}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={s.footer}>
          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
            {createdCount > 0 && <span style={{ color: '#16a34a', fontWeight: 600 }}>{createdCount} created</span>}
            {createdCount > 0 && selectedCount > 0 && ' | '}
            {selectedCount > 0 && `${selectedCount} selected`}
            {selectedCount > 0 && creatableCount < selectedCount && (
              <span style={{ color: '#d97706' }}> ({selectedCount - creatableCount} blocked)</span>
            )}
            {rows.length > 1 && creatableCount > 1 && (
              <span style={{ color: '#64748b', marginLeft: '0.5rem' }}>(will create as set)</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
            <button className="btn btn-sm btn-primary" onClick={createCutSheets}
              disabled={creating || creatableCount === 0}>
              {creating ? 'Creating...' : `Create ${creatableCount} Cut Sheet${creatableCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const s = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    backgroundColor: 'white', borderRadius: '12px', width: '950px', maxWidth: '95vw',
    maxHeight: '80vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
  },
  header: {
    padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  body: {
    flex: 1, overflow: 'auto', padding: '0.5rem',
  },
  footer: {
    padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  warning: {
    padding: '0.5rem 0.75rem', backgroundColor: '#fffbeb', border: '1px solid #f59e0b',
    borderRadius: '6px', fontSize: '0.78rem', color: '#92400e', marginBottom: '0.25rem',
  },
}
